import { tenantAdminPages } from "./navigation.ts";

/**
 * Which page the address bar is asking for.
 *
 * The same mechanism the dispatch console uses, and for the same two reasons. "Come back to the
 * page you were on" needs the page written down somewhere that survives leaving for the identity
 * provider and returning, and a URL is that somewhere. It is also what makes a page shareable
 * between two administrators looking at the same change.
 *
 * A query parameter rather than a path, for one practical reason: a path needs the server to answer
 * every path with the application's document, and this console is served as static files from a
 * gateway nobody here configures. `/?page=audit` is one document at one path and needs nothing.
 */

export const PAGE_PARAMETER = "page";

/** Where an operator lands with nothing asked for. The first page the rail offers. */
export const DEFAULT_PAGE_ID: string = tenantAdminPages[0]?.id ?? "brand";

const KNOWN_PAGES: ReadonlySet<string> = new Set(tenantAdminPages.map((page) => page.id));

/**
 * Read the page out of a URL, ignoring anything that is not a page.
 *
 * An unknown identifier falls back to the default rather than rendering the shell's "No such page",
 * because the two mean different things to the operator who sees them: a stale bookmark should open
 * the console, and a page the shell knows about but this operator cannot reach should say so. The
 * second is the shell's job and it still happens.
 */
export function pageIdIn(url: string): string {
  const asked = new URL(url).searchParams.get(PAGE_PARAMETER);
  return asked !== null && KNOWN_PAGES.has(asked) ? asked : DEFAULT_PAGE_ID;
}

/**
 * The same URL, asking for a different page.
 *
 * Everything else in the address is preserved. That matters more than it looks: this is also the URL
 * the sign-in flow stores as its return address, so dropping a query parameter here would silently
 * drop it from what an operator comes back to.
 */
export function urlForPage(url: string, pageId: string): string {
  const next = new URL(url);
  if (pageId === DEFAULT_PAGE_ID) next.searchParams.delete(PAGE_PARAMETER);
  else next.searchParams.set(PAGE_PARAMETER, pageId);
  return next.toString();
}
